import { useTranslation } from "react-i18next"
import { MinusIcon, TrendingDownIcon, TrendingUpIcon } from "lucide-react"

import { Card } from "@/components/ui/card"
import { cn } from "@/lib/cn"
import type { SupportedLocale } from "@/lib/datetime"
import type { Delta } from "@/lib/delta"
import { formatPercent } from "@/lib/format"

export interface SummaryItem {
  id: string
  label: string
  /** Already formatted (a count, a rate). */
  value: string
  delta: Delta
  /** Which way is good news: failures going up is not. */
  better?: "up" | "down"
}

const change = (d: Exclude<Delta, "no-comparison">): number => ("pct" in d ? d.pct : d.points)

/**
 * Figures against the previous window of the same length (LLD §8.1): the
 * change, coloured by whether it is good news, or "no comparison" when the
 * previous window had nothing to compare with, never +∞ %.
 */
export function SummaryCards({
  items,
  locale,
  against,
}: {
  items: SummaryItem[]
  locale: SupportedLocale
  /** What the change is measured against, said once under every figure. */
  against: string
}) {
  const { t } = useTranslation("common")
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {items.map((item) => {
        const d = item.delta
        const n = d === "no-comparison" ? 0 : change(d)
        const good = item.better === "down" ? n < 0 : n > 0
        const Icon = n > 0 ? TrendingUpIcon : n < 0 ? TrendingDownIcon : MinusIcon
        return (
          <Card key={item.id} data-stat-card="" className="min-w-0 gap-1 px-4 py-3.5">
            <p className="text-xs font-medium tracking-wide text-pretty break-words text-muted-foreground uppercase">
              {item.label}
            </p>
            <p className="numeric text-lg font-semibold text-foreground sm:text-2xl">{item.value}</p>
            {d === "no-comparison" ? (
              <p className="text-xs text-muted-foreground">{t("summary.noComparison")}</p>
            ) : (
              <p
                className={cn(
                  "flex flex-wrap items-center gap-1 text-xs",
                  n === 0 ? "text-muted-foreground" : good ? "text-success" : "text-destructive",
                )}
              >
                <Icon className="size-3" aria-hidden />
                <span className="numeric">
                  {"pct" in d
                    ? formatPercent(d.pct / 100, locale, { signed: true })
                    : t("summary.points", { value: formatPoints(d.points, locale) })}
                </span>
                <span className="text-muted-foreground">{against}</span>
              </p>
            )}
          </Card>
        )
      })}
    </div>
  )
}

const formatPoints = (points: number, locale: SupportedLocale): string =>
  formatPercent(points / 100, locale, { signed: true }).replace(/\s?%/, "")
